import * as _ from 'underscore'
import { Enums } from '../../../models/common/enums'
import { OpLocation } from '../../../models/core/op-location'
import { IOpObject, IReferenceType } from '../../../models/util/interfaces'
import { CommonHelper } from '../../../helpers/common-helper'
import { EventsNames } from '../../../helpers/events-names'
import { LocalizationHelper } from '../../../helpers/localization-helper'
import { LogHelper } from '../../../helpers/log-helper'
import { LocationService } from '../../../services/location'
import { UserSessionService } from '../../../services/user-session'
import { BulkEditInitParams, CreateEditInitParams } from '../../common/create-edit-popup/create-edit-popup.controller'
import { PopupType } from './../../../helpers/popup-manager'
import { CreateEditController, CreateEditScope } from '../../common/common.create-edit.controller'
import { LoggerService } from '../../common/common.module'

interface ILocationCreateEditScope
    extends CreateEditScope
{
}

export class LocationCreateEditController
    extends CreateEditController
{
    // #region Members

    private referenceValue: OpLocation;
    private isLocationCopy: boolean

    // #endregion

    constructor(
        private $scope: ILocationCreateEditScope,
        private $q: ng.IQService,
        private $timeout: ng.ITimeoutService,
        private logger: LoggerService,
        private userSession: UserSessionService,
        public referenctType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName,
        private locationService: LocationService
    ) {
        super(referenctType)

        this.setEvents()
        this.setOptions()
    }

    // #region Events

    public onInit(args: any[]): void {
        if (_.size(args) > 0) {
            const createEditInitParams: CreateEditInitParams = args[0]

            if (createEditInitParams.isCopy === true) {
                createEditInitParams.referenceObjectId = 0
                if (createEditInitParams.referenceObject != null) {
                    createEditInitParams.referenceObject.idObject = 0
                }
            }

            this.isLocationCopy = createEditInitParams.isCopy
            this.referenceValue = createEditInitParams != null ? <OpLocation>createEditInitParams.referenceObject : null
            this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.CREATE_EDIT_POPUP)

            if (this.isLocationCopy) {
                this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__COPYING, this.moduleName)
            }
            else if (this.referenceValue != null && this.referenceValue.idObject > 0) {
                this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__EDITING, this.moduleName)
            }
            else {
                this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__ADDING, this.moduleName)

                if (createEditInitParams.customDataSources != null) {
                    const locTypes: IReferenceType[] = createEditInitParams.customDataSources.tryGetValue(Enums.ReferenceType.IdLocationType)

                    if (_.size(locTypes) === 1) {
                        if (createEditInitParams.customDataSourcesDefaultValues == null) {
                            createEditInitParams.customDataSourcesDefaultValues = {}
                        }

                        createEditInitParams.customDataSourcesDefaultValues[Enums.DataType.HELPER_ID_LOCATION_TYPE] = locTypes[0].getReferenceKey()
                    }
                }
            }

            this.$timeout(() => {
                this.$scope.$broadcast(EventsNames.OPEN_CREATE_EDIT_POPUP, createEditInitParams)
            }, 100)
        }
    }

    public onInitBulk(args: any[]): void {
        if (_.size(args) > 0 && args[0] != null) {
            const bulkEditInitParams: BulkEditInitParams = args[0]

            this.$scope.$broadcast(EventsNames.SHOW_CREATE_EDIT_POPUP, PopupType.BULK_EDIT_POPUP)

            this.$scope.options.title = LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__MASS_EDITING, this.moduleName)

            this.$scope.gridData = args[0]

            if (_.size(args) > 1) {
                this.$scope.gridColumns = args[1]
            }

            this.$scope.$broadcast(EventsNames.OPEN_BULK_EDIT_POPUP, bulkEditInitParams)
        }
    }

    public onSave(objectToSave: IOpObject): ng.IPromise<IOpObject> {
        const deferred: ng.IDeferred<IOpObject> = this.$q.defer<IOpObject>()

        this.locationService
            .saveLocation(OpLocation.fromIOpObject(objectToSave), this.moduleName)
            .then((savedObject: OpLocation) => {
                CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.locationSaved, true, 0)
                this.$scope.$root.$broadcast(EventsNames.CLOSE_CREATE_EDIT_POPUP, savedObject)

                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                LogHelper.error(LocationService.LOG_PREFIX, error)
                deferred.reject(error)
            })

        return deferred.promise
    }

    public onBulkSave(objectToSave: IOpObject): ng.IPromise<IOpObject> {
        const deferred: ng.IDeferred<IOpObject> = this.$q.defer<IOpObject>()

        this.locationService
            .saveLocation(OpLocation.fromIOpObject(objectToSave), this.moduleName, true)
            .then((savedObject: OpLocation) => {
                deferred.resolve(savedObject)
            })
            .fail((error: any) => {
                LogHelper.error(LocationService.LOG_PREFIX, error)
                deferred.reject(error)
            })

        return deferred.promise
    }

    // #endregion

    // #region Setup

    public setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_CREATE_EDIT_POPUP, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onInit(args)
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_BULK_EDIT_POPUP, this.moduleName),
            (event: ng.IAngularEvent, ...args: any[]) => this.onInitBulk(args)
        )
    }

    public setOptions(): void {
        this.$scope.popupVisible = false
        this.$scope.options = {
            localStorageVarForSavedEntity: CommonHelper.localStorageVarForSavedEntity.locationSaved,
            onSubmit: null,
            onClose: null,
            referenceType: this.referenceType,
            submitBulkEdit: null,
            succsessMessage: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, this.moduleName),
            title: '',
            type: null,

            onBulkSave: (objectToSave: IOpObject) => this.onBulkSave(objectToSave),
            onSave: (objectToSave: IOpObject) => this.onSave(objectToSave)
        }
    }

    // #endregion
}
